import { ErrorText } from "features/bots/common/styles.ts";
import CustomInputNumber from "common/Inputs/CustomInputNumber.tsx";
import { useContext, useEffect, useMemo, useState } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import { isSymbol } from "@shared/types/utils.ts";
import {
  AmountWrapper,
  InputWrapper,
  StyledSelect,
} from "features/bots/BotForm/General/AmountSelector.tsx";
import { toFixed } from "@shared/utils/numbers.ts";
import Text from "common/Typography/Text.tsx";
import {
  SECONDS_IN_DAY,
  SECONDS_IN_HOUR,
  SECONDS_IN_MINUTE,
} from "@shared/common/constants.ts";
import { SelectValue } from "antd/es/select";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";
import { LanguageNameSpaces } from "@shared/types";

interface AutoCancelTradeTimeoutProps {
  errorMessage?: string;
}

const AutoCancelTradeTimeout = ({
  errorMessage,
}: AutoCancelTradeTimeoutProps) => {
  const { advancedSettings, setAdvancedSettings, initialized, isEdit } =
    useContext(BotFormContext)!;
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const [timeType, setTimeType] = useState(TimeUnit.HOURS);
  const [hasInitialized, setHasInitialized] = useState(false);

  const storedSeconds = advancedSettings.auto_cancel_timeout_seconds;
  const isMultiValue = isSymbol(storedSeconds);

  useEffect(() => {
    if (
      isEdit &&
      initialized &&
      !hasInitialized &&
      storedSeconds &&
      !isMultiValue
    ) {
      const seconds = parseFloat(storedSeconds);
      if (!isNaN(seconds) && seconds > 0) {
        const appropriateTimeUnit = getTimeUnitFromSeconds(seconds);
        setTimeType(appropriateTimeUnit);
      }
      setHasInitialized(true);
    }
  }, [isEdit, initialized, storedSeconds, isMultiValue, hasInitialized]);

  const displayValue = useMemo(() => {
    if (isMultiValue || !storedSeconds) {
      return "";
    }
    const seconds = parseFloat(storedSeconds);
    if (isNaN(seconds) || seconds <= 0) {
      return "";
    }
    const multiplier = TIME_UNITS_MULTIPLIERS[timeType];
    const converted = toFixed(seconds / multiplier, VALUE_PRECISION);
    return converted.toString();
  }, [storedSeconds, timeType, isMultiValue]);

  const showTilda = useMemo(() => {
    const parsedSeconds =
      storedSeconds && !isMultiValue ? parseFloat(storedSeconds) : undefined;
    const displayedNum = parseFloat(displayValue);
    if (!displayValue || isNaN(displayedNum) || !parsedSeconds) {
      return false;
    }
    const multiplier = TIME_UNITS_MULTIPLIERS[timeType];
    const rawInSelectedUnit = parsedSeconds / multiplier;
    return Math.abs(displayedNum - rawInSelectedUnit) > 1e-9;
  }, [storedSeconds, timeType, isMultiValue, displayValue]);

  const onTimeTypeChange = (newTimeType: SelectValue) => {
    if (newTimeType) {
      setTimeType(newTimeType as TimeUnit);
    }
  };

  const onChange = (value: string | undefined) => {
    if (!value || value === "") {
      setAdvancedSettings({
        ...advancedSettings,
        auto_cancel_timeout_seconds: undefined,
      });
      return;
    }

    const numberValue = parseFloat(value);
    if (isNaN(numberValue) || numberValue < 0) {
      return;
    }

    const multiplier = TIME_UNITS_MULTIPLIERS[timeType];
    const seconds = numberValue * multiplier;
    setAdvancedSettings({
      ...advancedSettings,
      auto_cancel_timeout_seconds: seconds.toString(),
    });
  };

  const amountValueInput = (
    <InputWrapper>
      {showTilda && <Text>~</Text>}
      <CustomInputNumber
        onChange={onChange}
        value={isMultiValue ? "" : displayValue}
        precision={VALUE_PRECISION}
        placeholder="No Limit"
        bordered={false}
        useDebounce={true}
        stringMode={true}
      />
    </InputWrapper>
  );

  const amountTypeSelect = (
    <StyledSelect
      value={timeType}
      onChange={onTimeTypeChange}
      variant="borderless"
      options={Object.values(TimeUnit).map((timeUnit) => ({
        value: timeUnit,
        label: t(TIME_UNIT_TO_DISPLAY_NAME[timeUnit]),
      }))}
      mobileTitle={t("time_units.title")}
      showSearch={false}
      $isSplitInput={true}
      popupMatchSelectWidth={false}
    />
  );

  return (
    <div>
      <AmountWrapper>
        {amountValueInput}
        {amountTypeSelect}
      </AmountWrapper>
      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </div>
  );
};

export default AutoCancelTradeTimeout;

const VALUE_PRECISION = 2;

enum TimeUnit {
  SECONDS = "Seconds",
  MINUTES = "Minutes",
  HOURS = "Hours",
  DAYS = "Days",
}

const TIME_UNITS_MULTIPLIERS = {
  [TimeUnit.SECONDS]: 1,
  [TimeUnit.MINUTES]: SECONDS_IN_MINUTE,
  [TimeUnit.HOURS]: SECONDS_IN_HOUR,
  [TimeUnit.DAYS]: SECONDS_IN_DAY,
};

const TIME_UNIT_TO_DISPLAY_NAME: Record<
  TimeUnit,
  TranslationKeys<LanguageNameSpaces.BOTS>
> = {
  [TimeUnit.SECONDS]: "time_units.seconds",
  [TimeUnit.MINUTES]: "time_units.minutes",
  [TimeUnit.HOURS]: "time_units.hours",
  [TimeUnit.DAYS]: "time_units.days",
};

const getTimeUnitFromSeconds = (seconds: number): TimeUnit => {
  if (seconds >= SECONDS_IN_DAY) {
    return TimeUnit.DAYS;
  }
  if (seconds >= SECONDS_IN_HOUR) {
    return TimeUnit.HOURS;
  }
  if (seconds >= SECONDS_IN_MINUTE) {
    return TimeUnit.MINUTES;
  }
  return TimeUnit.SECONDS;
};
